import { useMemo } from "react";
import type { Appointment } from "@/hooks/useAdmin";
import { getTodaysAppointments } from "@/hooks/useAdmin";
import {
  CalendarDays,
  Clock,
  Phone,
  MapPin,
  User,
  AlertCircle,
  ArrowRight,
} from "lucide-react";

interface TodaysAppointmentsWidgetProps {
  appointments: Appointment[];
  onStatusChange: (id: string, status: Appointment["status"]) => void;
}

export default function TodaysAppointmentsWidget({
  appointments,
  onStatusChange,
}: TodaysAppointmentsWidgetProps) {
  const todays = useMemo(() => getTodaysAppointments(appointments), [appointments]);

  const totalToday = todays.length;
  const confirmed = todays.filter((a) => a.status === "confirmed").length;
  const scheduled = todays.filter((a) => a.status === "scheduled").length;

  const statusBadge: Record<string, string> = {
    scheduled: "bg-amber-100 text-amber-700 border-amber-200",
    confirmed: "bg-emerald-100 text-emerald-700 border-emerald-200",
    completed: "bg-blue-100 text-blue-700 border-blue-200",
    cancelled: "bg-red-100 text-red-700 border-red-200",
    no_show: "bg-slate-100 text-slate-600 border-slate-200",
  };

  const now = new Date();
  const currentHour = now.getHours();
  const currentMin = now.getMinutes();

  const isUpcoming = (timeStr: string) => {
    const [h, m, period] = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i)?.slice(1) || ["0", "0", "AM"];
    let hour = parseInt(h);
    if (period.toUpperCase() === "PM" && hour !== 12) hour += 12;
    if (period.toUpperCase() === "AM" && hour === 12) hour = 0;
    const aptMinutes = hour * 60 + parseInt(m);
    const nowMinutes = currentHour * 60 + currentMin;
    return aptMinutes > nowMinutes;
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-teal-600" />
            Today's Appointments
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {totalToday} appointments for {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "short" })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {scheduled > 0 && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
              {scheduled} Pending
            </span>
          )}
          {confirmed > 0 && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">
              {confirmed} Confirmed
            </span>
          )}
        </div>
      </div>

      {totalToday === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-3">
            <AlertCircle className="w-5 h-5 text-slate-400" />
          </div>
          <p className="text-sm text-slate-500 font-medium">No appointments today</p>
          <p className="text-xs text-slate-400 mt-1">All caught up! Check upcoming days for future bookings.</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
          {todays.map((apt) => {
            const upcoming = isUpcoming(apt.preferred_time);
            return (
              <div
                key={apt.id}
                className={`border rounded-lg p-3.5 transition-colors ${
                  upcoming
                    ? "border-teal-200 bg-teal-50/40"
                    : "border-slate-200 bg-white hover:bg-slate-50"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm text-slate-900">{apt.name}</span>
                      <span
                        className={`text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize border ${
                          statusBadge[apt.status] || "bg-slate-100 text-slate-600 border-slate-200"
                        }`}
                      >
                        {apt.status}
                      </span>
                      {upcoming && apt.status !== "completed" && apt.status !== "cancelled" && (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-teal-100 text-teal-700 border border-teal-200">
                          Upcoming
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3 text-slate-400" />
                        {apt.preferred_time}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-slate-400" />
                        {apt.city}
                      </span>
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3 text-slate-400" />
                        {apt.phone}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 mt-1 text-xs text-slate-600">
                      <User className="w-3 h-3 text-slate-400" />
                      {apt.insurance_type}
                      {apt.source && (
                        <span className="text-slate-400 ml-1">· {apt.source}</span>
                      )}
                    </div>
                    {apt.notes && (
                      <div className="text-[11px] text-slate-500 mt-1.5 bg-slate-50 rounded px-2 py-1.5">
                        {apt.notes}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-1.5 shrink-0">
                    {apt.status === "scheduled" && (
                      <button
                        onClick={() => onStatusChange(apt.id, "confirmed")}
                        className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold rounded-md transition-colors whitespace-nowrap"
                      >
                        Confirm
                      </button>
                    )}
                    {apt.status === "confirmed" && (
                      <button
                        onClick={() => onStatusChange(apt.id, "completed")}
                        className="px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-semibold rounded-md transition-colors whitespace-nowrap"
                      >
                        Complete
                      </button>
                    )}
                    {["scheduled", "confirmed"].includes(apt.status) && (
                      <button
                        onClick={() => onStatusChange(apt.id, "cancelled")}
                        className="px-2.5 py-1 border border-red-200 text-red-600 hover:bg-red-50 text-[11px] font-semibold rounded-md transition-colors whitespace-nowrap"
                      >
                        Cancel
                      </button>
                    )}
                    <a
                      href={`/admin/appointments`}
                      className="flex items-center justify-center gap-1 text-[11px] text-slate-500 hover:text-slate-700 transition-colors"
                    >
                      View <ArrowRight className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}